import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { buildApiUrl } from '../utils/api';
import { 
  Card, 
  Form, 
  Input, 
  Select, 
  Button, 
  Upload, 
  Checkbox, 
  DatePicker, 
  Row, 
  Col, 
  Space, 
  Typography,
  message 
} from 'antd';
import { 
  FileTextOutlined, 
  UploadOutlined, 
  SendOutlined,
  FormOutlined
} from '@ant-design/icons';
import './Chamados.css';
import fundoNovagGeracao from '../images/FUNDO_NOVAGERACAO_SEMPRE.png';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Paragraph } = Typography;

const Chamados = () => {
  const { user } = useUser();
  const [form] = Form.useForm();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formularioSelecionado, setFormularioSelecionado] = useState('');
  const [tipoSolicitacao, setTipoSolicitacao] = useState('');
  const [notaFiscal, setNotaFiscal] = useState('');
  const [formaPagamento, setFormaPagamento] = useState('');
  
  // Arquivos anexados
  const [anexoNotasEmitida, setAnexoNotasEmitida] = useState([]);
  const [anexoNotasAEmitir, setAnexoNotasAEmitir] = useState([]);
  const [anexoCampanha, setAnexoCampanha] = useState([]);
  const [anexoServico, setAnexoServico] = useState([]);
  const [anexoMaterial, setAnexoMaterial] = useState([]);
  
  // Estado para armazenar o token Azure em memória (mais seguro que localStorage)
  const [azureToken, setAzureToken] = useState(null);
  
  // Função para obter o token Azure do usuário logado
  const getAzureToken = async () => {
    // Se já temos o token em memória, retornar
    if (azureToken) {
      return azureToken;
    }
    
    // Tentar obter do sessionStorage (mais seguro que localStorage)
    const storedToken = sessionStorage.getItem('azureToken');
    if (storedToken) {
      setAzureToken(storedToken);
      return storedToken;
    }
    
    // Se não temos o token, buscar do backend (token do usuário logado)
    try {
      const requestHeaders = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };
      
      const response = await fetch(buildApiUrl('/api/azure-token'), {
        method: 'GET',
        headers: requestHeaders
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Buscar o token Azure do usuário (não do sistema)
        const newToken = data.azureToken || data.token || data.access_token;
        
        if (newToken) {
          // Armazenar em memória e sessionStorage (mais seguro)
          setAzureToken(newToken);
          sessionStorage.setItem('azureToken', newToken);
          return newToken;
        } else {
          throw new Error('Token Azure do usuário não retornado pelo backend');
        }
      } else {
        throw new Error(`Erro HTTP: ${response.status} - ${response.statusText}`);
      }
    } catch (error) {
      throw error;
    }
  };
  
  // Função para limpar o token Azure do usuário
  const clearAzureToken = () => {
    setAzureToken(null);
    sessionStorage.removeItem('azureToken');
  };
  
  // Limpar token Azure do usuário quando o componente for desmontado
  useEffect(() => {
    return () => {
      clearAzureToken();
    };
  }, []);
  
  // Verificar se o usuário está logado
  if (!user) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: 'white',
        fontSize: '18px'
      }}>
        <div>Carregando usuário...</div>
      </div>
    );
  }

  // Lista de materiais disponíveis
  const materiaisDisponiveis = [
    'ADESIVO',
    'AJUSTE SITE',
    'APRESENTAÇÃO',
    'BANNER',
    'BANNER SITE',
    'BRINDE',
    'CAMPANHA DE VENDAS',
    'CARD',
    'CARTÃO DE VISITA',
    'CONVITE',
    'CRACHÁ',
    'DOCUMENTO',
    'EDIÇÃO DE VÍDEO',
    'FIGURINHA',
    'FLIPCHART',
    'FLYER',
    'FOLDER',
    'FUNDO DE TELA - CELULAR',
    'FUNDO DE TELA - COMPUTADOR',
    'INFOGRÁFICO',
    'LANDING PAGE',
    'LOGO',
    'LONA',
    'MANUAL',
    'MATERIA BLOG',
    'MINIDOOR',
    'OUTDOOR',
    'PLACA',
    'PLANILHA',
    'PLOTAGEM CARRO',
    'PUBLICAÇÃO REDES SOCIAIS',
    'PUBLICAÇÃO SITE',
    'ROTEIRO',
    'SACARIA',
    'SITE',
    'STORIE',
    'TOTEM',
    'VÍDEO'
  ];

  // Validar arquivos antes do upload
  const beforeUpload = (file) => {
    const maxFileSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'video/mp4',
      'video/avi',
      'video/mov',
      'audio/mpeg',
      'audio/mp3',
      'audio/wav'
    ];

    if (file.size > maxFileSize) {
      message.error(`${file.name} excede o tamanho máximo de 10MB`);
      return Upload.LIST_IGNORE;
    }

    if (!allowedTypes.includes(file.type)) {
      message.error(`${file.name} não é um tipo de arquivo permitido`);
      return Upload.LIST_IGNORE;
    }

    return false; // Impedir upload automático
  };

  // Props para o componente Upload do Ant Design
  const uploadProps = (setFileList) => ({
    beforeUpload,
    multiple: true,
    maxCount: 10,
    onRemove: (file) => {
      setFileList((list) => {
        const index = list.indexOf(file);
        const newFileList = list.slice();
        newFileList.splice(index, 1);
        return newFileList;
      });
    },
    onChange: ({ fileList }) => {
      setFileList(fileList.filter(f => f.status !== 'error'));
    },
  });

  const handleSubmit = async (values) => {
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const userEmail = user?.email || '';

      if (!userEmail) {
        message.error('Erro: Email do usuário não encontrado. Faça login novamente.');
        setIsSubmitting(false);
        return;
      }

      const formDataToSend = new FormData();
      
      // Campo obrigatório: assunto
      if (values.titulo && values.titulo.trim()) {
        formDataToSend.append('assunto', values.titulo.trim());
      }
       
      // Campo obrigatório: descricao
      if (values.descricaoDemanda && values.descricaoDemanda.trim()) {
        formDataToSend.append('descricao', values.descricaoDemanda.trim());
      }
       
      // Campo obrigatório: categoria (mapear do formulário)
      if (values.formulario) {
        formDataToSend.append('categoria', values.formulario);
      }
       
      // Adicionar campos adicionais que podem ser úteis
      if (values.tipoSolicitacao) {
        formDataToSend.append('tipoSolicitacao', values.tipoSolicitacao);
      }
       
      // Adicionar TODOS os campos adicionais que o usuário preencheu
      if (values.contratante && values.contratante.trim()) {
        formDataToSend.append('contratante', values.contratante.trim());
      }
       
      if (values.formaPagamento) {
        formDataToSend.append('formaPagamento', values.formaPagamento);
      }
       
      if (values.dadosPagamento && values.dadosPagamento.trim()) {
        formDataToSend.append('dadosPagamento', values.dadosPagamento.trim());
      }
       
      if (values.dataPagamento) {
        formDataToSend.append('dataPagamento', values.dataPagamento.format('YYYY-MM-DD'));
      }
       
      if (values.notaFiscal) {
        formDataToSend.append('notaFiscal', values.notaFiscal);
      }
       
      if (values.motivoNaoEmissao && values.motivoNaoEmissao.trim()) {
        formDataToSend.append('motivoNaoEmissao', values.motivoNaoEmissao.trim());
      }
       
      if (values.quantidadeTamanho && values.quantidadeTamanho.trim()) {
        formDataToSend.append('quantidadeTamanho', values.quantidadeTamanho.trim());
      }
       
      if (values.prazoEntrega) {
        formDataToSend.append('prazoEntrega', values.prazoEntrega.format('YYYY-MM-DD'));
      }
       
      // Adicionar anexos se existirem
      if (anexoNotasEmitida.length > 0) {
        anexoNotasEmitida.forEach(file => formDataToSend.append('anexo', file.originFileObj));
      }
      if (anexoNotasAEmitir.length > 0) {
        anexoNotasAEmitir.forEach(file => formDataToSend.append('anexo', file.originFileObj));
      }
      if (anexoCampanha.length > 0) {
        anexoCampanha.forEach(file => formDataToSend.append('anexo', file.originFileObj));
      }
      if (anexoServico.length > 0) {
        anexoServico.forEach(file => formDataToSend.append('anexo', file.originFileObj));
      }
      if (anexoMaterial.length > 0) {
        anexoMaterial.forEach(file => formDataToSend.append('anexo', file.originFileObj));
      }
       
      // Adicionar materiais se existirem
      if (values.materiais && values.materiais.length > 0) {
        values.materiais.forEach(material => formDataToSend.append('materiais', material));
      }

      // Obter o token Azure do usuário logado
      let userAzureToken;
        
      // Primeiro tentar obter do localStorage (capturado durante login)
      const storedAzureToken = localStorage.getItem('azureToken');
      const azureTokenExpires = localStorage.getItem('azureTokenExpires');
        
      if (storedAzureToken && azureTokenExpires) {
        // Verificar se o token não expirou
        const now = Date.now();
        const expiresAt = parseInt(azureTokenExpires);
          
        if (now < expiresAt) {
          userAzureToken = storedAzureToken;
        } else {
          try {
            userAzureToken = await getAzureToken();
          } catch (error) {
            message.error('Erro: Falha ao obter novo token Azure. Tente novamente ou faça login novamente.');
            setIsSubmitting(false);
            return;
          }
        }
      } else {
        // Se não tem token no localStorage, buscar via API
        try {
          userAzureToken = await getAzureToken();
        } catch (error) {
          message.error('Erro: Falha ao obter token Azure. Tente novamente ou faça login novamente.');
          setIsSubmitting(false);
          return;
        }
      }
        
      const response = await fetch(buildApiUrl('/api/support/contact'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-User-Email': userEmail,
          'x-azure-token': userAzureToken // Token Azure do usuário logado
        },
        body: formDataToSend
      });

      if (response.ok) {
        message.success('Chamado criado com sucesso!');
        // Limpar formulário
        form.resetFields();
        setFormularioSelecionado('');
        setTipoSolicitacao('');
        setNotaFiscal('');
        setFormaPagamento('');
        setAnexoNotasEmitida([]);
        setAnexoNotasAEmitir([]);
        setAnexoCampanha([]);
        setAnexoServico([]);
        setAnexoMaterial([]);
      } else {
        const errorData = await response.json();
        message.error(`Erro ao criar chamado: ${errorData.message || 'Erro desconhecido'}`);
      }
    } catch (error) {
      message.error('Erro ao criar chamado. Verifique sua conexão e tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="chamados-container">
      <Card 
        className="chamados-header-card"
        style={{
          marginBottom: 24,
          background: `linear-gradient(135deg, rgba(45, 95, 63, 0.9) 0%, rgba(74, 124, 89, 0.9) 100%), url(${fundoNovagGeracao})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          border: 'none'
        }}
      >
        <Row justify="center">
          <Col>
            <Space direction="vertical" align="center" size={4}>
              <Title level={2} style={{ color: '#fff', margin: 0 }}>
                <FormOutlined /> Solicitações de Marketing
              </Title>
              <Paragraph style={{ color: '#fff', margin: 0, opacity: 0.9 }}>
                Abra uma nova solicitação de suporte para nossa equipe de Marketing e para lançamento de notas e despesas
              </Paragraph>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          {/* Informações Básicas */}
          <Card 
            type="inner" 
            title={<><FileTextOutlined /> Informações Básicas</>}
            style={{ marginBottom: 24 }}
          >
            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  label="Título"
                  name="titulo"
                  rules={[{ required: true, message: 'Título é obrigatório' }]}
                >
                  <Input placeholder="Insira o título do chamado" />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  label="Formulário"
                  name="formulario"
                  rules={[{ required: true, message: 'Selecione o tipo de formulário' }]}
                >
                  <Select 
                    placeholder="Selecione..."
                    onChange={(value) => {
                      setFormularioSelecionado(value);
                      setTipoSolicitacao('');
                      setNotaFiscal('');
                      setFormaPagamento('');
                      
                      // Limpar todos os campos dependentes do Form
                      form.setFieldsValue({
                        tipoSolicitacao: undefined,
                        notaFiscal: undefined,
                        formaPagamento: undefined,
                        dadosPagamento: undefined,
                        contratante: undefined,
                        dataPagamento: undefined,
                        descricaoDemanda: undefined,
                        motivoNaoEmissao: undefined,
                        materiais: undefined,
                        quantidadeTamanho: undefined,
                        prazoEntrega: undefined
                      });
                      
                      // Limpar anexos
                      setAnexoNotasEmitida([]);
                      setAnexoNotasAEmitir([]);
                      setAnexoCampanha([]);
                      setAnexoServico([]);
                      setAnexoMaterial([]);
                    }}
                  >
                    <Option value="Solicitações de Marketing">Solicitações de Marketing</Option>
                    <Option value="Lançamento de Notas e Despesas">Lançamento de Notas e Despesas</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Lançamento de Notas e Despesas */}
          {formularioSelecionado === 'Lançamento de Notas e Despesas' && (
            <Card 
              type="inner" 
              title={<><FileTextOutlined /> Dados do Lançamento</>}
              style={{ marginBottom: 24 }}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Contratante do serviço"
                    name="contratante"
                    rules={[{ required: true, message: 'Contratante é obrigatório' }]}
                  >
                    <Input placeholder="Insira o contratante" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Forma de Pagamento"
                    name="formaPagamento"
                    rules={[{ required: true, message: 'Forma de pagamento é obrigatória' }]}
                  >
                    <Select 
                      placeholder="Selecione..."
                      onChange={(value) => {
                        setFormaPagamento(value);
                        
                        // Limpar dados de pagamento se não for Pix
                        if (value !== 'Pix') {
                          form.setFieldsValue({
                            dadosPagamento: undefined
                          });
                        }
                      }}
                    >
                      <Option value="Pix">Pix</Option>
                      <Option value="Boleto">Boleto</Option>
                      <Option value="Transferencia bancária (TED)">Transferência bancária (TED)</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {formaPagamento === 'Pix' && (
                <Row gutter={16}>
                  <Col xs={24}>
                    <Form.Item
                      label="Dados de pagamento"
                      name="dadosPagamento"
                      rules={[{ required: true, message: 'Dados de pagamento são obrigatórios' }]}
                    >
                      <Input placeholder="Insira os dados do Pix" />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Data de pagamento"
                    name="dataPagamento"
                    rules={[{ required: true, message: 'Data de pagamento é obrigatória' }]}
                  >
                    <DatePicker 
                      style={{ width: '100%' }}
                      format="DD/MM/YYYY"
                      placeholder="Selecione a data"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Nota Fiscal Emitida ou a Emitir?"
                    name="notaFiscal"
                    rules={[{ required: true, message: 'Selecione o status da nota fiscal' }]}
                  >
                    <Select 
                      placeholder="Selecione..."
                      onChange={(value) => {
                        setNotaFiscal(value);
                        
                        // Limpar campos específicos
                        form.setFieldsValue({
                          descricaoDemanda: undefined,
                          motivoNaoEmissao: undefined
                        });
                        
                        // Limpar anexos
                        setAnexoNotasEmitida([]);
                        setAnexoNotasAEmitir([]);
                      }}
                    >
                      <Option value="Emitida">Emitida</Option>
                      <Option value="À emitir">À emitir</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {notaFiscal === 'Emitida' && (
                <>
                  <Row gutter={16}>
                    <Col xs={24}>
                      <Form.Item
                        label="Descrição da demanda"
                        name="descricaoDemanda"
                        rules={[{ required: true, message: 'Descrição da demanda é obrigatória' }]}
                      >
                        <TextArea rows={4} placeholder="Descreva a demanda" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24}>
                      <Form.Item label="Anexo (opcional)">
                        <Upload {...uploadProps(setAnexoNotasEmitida)} fileList={anexoNotasEmitida}>
                          <Button icon={<UploadOutlined />}>Selecionar Arquivos</Button>
                        </Upload>
                        <small style={{ color: '#888', fontSize: '12px' }}>
                          Limite: 10 arquivos | Tamanho máximo: 10MB | Tipos: Word, Excel, PPT, PDF, Imagem, Vídeo, Áudio
                        </small>
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}

              {notaFiscal === 'À emitir' && (
                <>
                  <Row gutter={16}>
                    <Col xs={24}>
                      <Form.Item
                        label="Motivo da não emissão da nota fiscal"
                        name="motivoNaoEmissao"
                        rules={[{ required: true, message: 'Motivo da não emissão é obrigatório' }]}
                      >
                        <TextArea rows={4} placeholder="Insira o motivo" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24}>
                      <Form.Item
                        label="Descrição da demanda"
                        name="descricaoDemanda"
                        rules={[{ required: true, message: 'Descrição da demanda é obrigatória' }]}
                      >
                        <TextArea rows={4} placeholder="Descreva a demanda" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24}>
                      <Form.Item label="Anexo (opcional)">
                        <Upload {...uploadProps(setAnexoNotasAEmitir)} fileList={anexoNotasAEmitir}>
                          <Button icon={<UploadOutlined />}>Selecionar Arquivos</Button>
                        </Upload>
                        <small style={{ color: '#888', fontSize: '12px' }}>
                          Limite: 10 arquivos | Tamanho máximo: 10MB | Tipos: Word, Excel, PPT, PDF, Imagem, Vídeo, Áudio
                        </small>
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}
            </Card>
          )}

          {/* Solicitações de Marketing */}
          {formularioSelecionado === 'Solicitações de Marketing' && (
            <Card 
              type="inner" 
              title={<><FileTextOutlined /> Dados da Solicitação</>}
              style={{ marginBottom: 24 }}
            >
              <Row gutter={16}>
                <Col xs={24}>
                  <Form.Item
                    label="Tipo de solicitação"
                    name="tipoSolicitacao"
                    rules={[{ required: true, message: 'Tipo de solicitação é obrigatório' }]}
                  >
                    <Select 
                      placeholder="Selecione..."
                      onChange={(value) => {
                        setTipoSolicitacao(value);
                        
                        // Limpar campos específicos de cada tipo
                        form.setFieldsValue({
                          descricaoDemanda: undefined,
                          materiais: undefined,
                          quantidadeTamanho: undefined,
                          prazoEntrega: undefined
                        });
                        
                        // Limpar anexos
                        setAnexoCampanha([]);
                        setAnexoServico([]);
                        setAnexoMaterial([]);
                      }}
                    >
                      <Option value="Campanha">Campanha</Option>
                      <Option value="Material">Material</Option>
                      <Option value="Serviço">Serviço</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {tipoSolicitacao === 'Campanha' && (
                <>
                  <Row gutter={16}>
                    <Col xs={24}>
                      <Form.Item
                        label="Descrição da demanda"
                        name="descricaoDemanda"
                        rules={[{ required: true, message: 'Descrição da demanda é obrigatória' }]}
                      >
                        <TextArea rows={4} placeholder="Descreva a demanda" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24}>
                      <Form.Item label="Anexo (opcional)">
                        <Upload {...uploadProps(setAnexoCampanha)} fileList={anexoCampanha}>
                          <Button icon={<UploadOutlined />}>Selecionar Arquivos</Button>
                        </Upload>
                        <small style={{ color: '#888', fontSize: '12px' }}>
                          Limite: 10 arquivos | Tamanho máximo: 10MB
                        </small>
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}

              {tipoSolicitacao === 'Serviço' && (
                <>
                  <Row gutter={16}>
                    <Col xs={24}>
                      <Form.Item
                        label="Descrição da demanda"
                        name="descricaoDemanda"
                        rules={[{ required: true, message: 'Descrição da demanda é obrigatória' }]}
                      >
                        <TextArea rows={4} placeholder="Descreva a demanda" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24}>
                      <Form.Item label="Anexo (opcional)">
                        <Upload {...uploadProps(setAnexoServico)} fileList={anexoServico}>
                          <Button icon={<UploadOutlined />}>Selecionar Arquivos</Button>
                        </Upload>
                        <small style={{ color: '#888', fontSize: '12px' }}>
                          Limite: 10 arquivos | Tamanho máximo: 10MB
                        </small>
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}

              {tipoSolicitacao === 'Material' && (
                <>
                  <Row gutter={16}>
                    <Col xs={24}>
                      <Form.Item
                        label="Selecione os materiais"
                        name="materiais"
                        rules={[{ required: true, message: 'Selecione pelo menos um material' }]}
                      >
                        <Checkbox.Group style={{ width: '100%' }}>
                          <Row gutter={[8, 8]}>
                            {materiaisDisponiveis.map(material => (
                              <Col xs={24} sm={12} md={8} key={material}>
                                <Checkbox value={material} style={{ whiteSpace: 'normal' }}>
                                  {material}
                                </Checkbox>
                              </Col>
                            ))}
                          </Row>
                        </Checkbox.Group>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24}>
                      <Form.Item
                        label="Descreva a quantidade e tamanho dos itens solicitados"
                        name="quantidadeTamanho"
                        rules={[{ required: true, message: 'Descrição da quantidade e tamanho é obrigatória' }]}
                      >
                        <TextArea rows={4} placeholder="Ex: 100 unidades, tamanho A4" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Prazo de entrega"
                        name="prazoEntrega"
                        rules={[{ required: true, message: 'Prazo de entrega é obrigatório' }]}
                      >
                        <DatePicker 
                          style={{ width: '100%' }}
                          format="DD/MM/YYYY"
                          placeholder="Selecione a data"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24}>
                      <Form.Item
                        label="Descrição da demanda"
                        name="descricaoDemanda"
                        rules={[{ required: true, message: 'Descrição da demanda é obrigatória' }]}
                      >
                        <TextArea rows={4} placeholder="Descreva a demanda" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24}>
                      <Form.Item label="Anexo (opcional)">
                        <Upload {...uploadProps(setAnexoMaterial)} fileList={anexoMaterial}>
                          <Button icon={<UploadOutlined />}>Selecionar Arquivos</Button>
                        </Upload>
                        <small style={{ color: '#888', fontSize: '12px' }}>
                          Limite: 10 arquivos | Tamanho máximo: 10MB
                        </small>
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}
            </Card>
          )}

          <Row justify="center" style={{ marginTop: 24 }}>
            <Col>
              <Space>
                <Button 
                  type="primary" 
                  size="large"
                  htmlType="submit"
                  icon={<SendOutlined />}
                  loading={isSubmitting}
                  style={{ minWidth: 200 }}
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar Chamado'}
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default Chamados;

